/**
 * Message query operations - Create
 */

import { v4 as uuidv4 } from 'uuid';
import type { ChatId, MessageRole } from '../../types';
import { getSupabaseClient } from '../../supabase-client';
import type { StoredMessage } from '../types';
import { logger } from '../../../logger';

/**
 * Conflict target for the message upsert.
 *
 * `message_id` is the STABLE UIMessage id from the AI SDK / client, unique per
 * chat. Inserting with `ignoreDuplicates` against it is what stops a re-saved
 * conversation from writing the whole history again — the failure that made
 * reloaded chats repeat every earlier turn once per turn.
 */
const MESSAGE_CONFLICT_TARGET = 'chat_id,message_id';

/**
 * Create a single message
 */
export async function createMessage(
  chatId: ChatId,
  role: MessageRole,
  parts: Array<Record<string, unknown>>,
  timestamp: string,
  metadata?: Record<string, unknown>,
  messageId?: string
): Promise<StoredMessage> {
  try {
    const client = getSupabaseClient();

    const { data, error } = await client
      .from('meridian_messages')
      .upsert(
        {
          id: uuidv4(),
          chat_id: chatId,
          message_id: messageId ?? uuidv4(),
          role,
          parts,
          timestamp,
          metadata,
          created_at: new Date().toISOString(),
        },
        { onConflict: MESSAGE_CONFLICT_TARGET, ignoreDuplicates: true }
      )
      .select()
      .maybeSingle();

    if (error) {
      throw error;
    }

    // `ignoreDuplicates` returns no row when the message was already stored.
    // Read the existing row back so callers always get the persisted record.
    if (!data) {
      const { data: existing, error: readError } = await client
        .from('meridian_messages')
        .select('*')
        .eq('chat_id', chatId)
        .eq('message_id', messageId ?? '')
        .maybeSingle();
      if (readError) {
        throw readError;
      }
      if (existing) {
        return existing as StoredMessage;
      }
      throw new Error('message upsert returned no row');
    }

    return data as StoredMessage;
  } catch (error) {
    logger.error('error creating message', error, { op: 'createMessage' });
    throw error;
  }
}

/**
 * Create multiple messages in a single operation.
 *
 * Idempotent per (chat_id, message_id): re-saving a conversation that already
 * contains some of these messages inserts only the genuinely new ones.
 */
export async function createMessages(
  messages: Array<{
    chatId: ChatId;
    role: MessageRole;
    parts: Array<Record<string, unknown>>;
    timestamp: string;
    metadata?: Record<string, unknown>;
    messageId?: string;
    /** Model + backend that produced an assistant message; omit for user rows. */
    modelId?: string | null;
    provider?: string | null;
  }>
): Promise<StoredMessage[]> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const messagesToInsert = messages.map((msg) => ({
      id: uuidv4(),
      chat_id: msg.chatId,
      message_id: msg.messageId ?? uuidv4(),
      role: msg.role,
      parts: msg.parts,
      timestamp: msg.timestamp,
      metadata: msg.metadata,
      model_id: msg.modelId ?? null,
      provider: msg.provider ?? null,
      created_at: now,
    }));

    // A single payload can legitimately repeat a message id (e.g. a history
    // merge that already contains the streamed copy). ON CONFLICT cannot fire
    // twice for the same row within one statement, so de-dupe here first.
    const seen = new Set<string>();
    const deduped = messagesToInsert.filter((row) => {
      const key = `${row.chat_id}::${row.message_id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const { data, error } = await client
      .from('meridian_messages')
      .upsert(deduped, {
        onConflict: MESSAGE_CONFLICT_TARGET,
        ignoreDuplicates: true,
      })
      .select();

    if (error) {
      throw error;
    }

    return (data || []) as StoredMessage[];
  } catch (error) {
    logger.error('error creating messages', error, { op: 'createMessages' });
    throw error;
  }
}
